import {
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
  Show,
  type Accessor,
  type VoidComponent,
} from "solid-js";
import { SolidLeafletMap } from "solidjs-leaflet";
import ApiService from "../../services/ApiService";
import mapPin from "../../assets/map-pin.svg?url";
import { isNil } from "lodash-es";
import { createGeolocationWatcher } from "@solid-primitives/geolocation";
import type { ApiCoordinates, MapStyle, TileConfiguration } from "../../types/Location";
import * as L from "leaflet";
import type { LeafletMouseEvent, Map as LMap } from "leaflet";
import { seconds } from "../../utils/timeUtil";

type MapProperties = {
  ref?: (api: MapApi) => void;
  onMapClick?: (coordinates: ApiCoordinates) => void;
  onLocationPermissionDenied?: () => void;
};

const markerLayer = L.layerGroup();
const locationLayer = L.layerGroup();
const icon = L.icon({
  iconUrl: mapPin,
  iconSize: [42, 49],
  iconAnchor: [21, 54],
});
const FALLBACK_LOCATION_CENTRAL_EUROPE: [number, number] = [49.1817, 12.876];
export const DEFAULT_ZOOM = 4;
const DEFAULT_LOCATION_ZOOM = 16;
export const MAX_ZOOM = 20;
export const MIN_ZOOM = 0;

export type MapApi = {
  recenter: () => void;
  setLocation: (coordinates: ApiCoordinates) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  setMapStyle: (mapStyle: MapStyle) => void;
  setMarker: (coordinates: ApiCoordinates) => void;
  clearMarker: () => void;
  zoom: Accessor<number>;
};

const LocationMap: VoidComponent<MapProperties> = (props) => {
  const getLocationConfiguration = ApiService.getLocationConfiguration();
  const [mapStyle, setMapStyle] = createSignal<MapStyle>("STREET");
  const [mapInstance, setMapInstance] = createSignal<LMap | null>(null);
  const [zoom, setZoom] = createSignal(DEFAULT_ZOOM);
  const geolocation = createGeolocationWatcher(true, {
    enableHighAccuracy: true,
    maximumAge: seconds(90),
    timeout: seconds(10),
  });

  onMount(() => {
    props.ref?.({
      recenter,
      setLocation,
      setMapStyle,
      zoomIn: () => mapInstance()?.zoomIn(),
      zoomOut: () => mapInstance()?.zoomOut(),
      setMarker,
      clearMarker,
      zoom,
    });
  });

  createEffect(() => {
    const error = geolocation.error;
    if (isNil(error)) {
      return;
    }

    if (error.code === error.PERMISSION_DENIED) {
      props.onLocationPermissionDenied?.();
    }
  });

  const currentLocation = createMemo<[number, number]>(() => {
    const l = geolocation.location;
    return !isNil(l) ? [l.latitude, l.longitude] : FALLBACK_LOCATION_CENTRAL_EUROPE;
  });

  createEffect(() => {
    mapInstance()?.setView(currentLocation());
  });

  createEffect(() => {
    const map = mapInstance();
    if (isNil(map)) {
      return;
    }

    const handleZoomEnd = () => setZoom(map.getZoom());
    map.on("zoomend", handleZoomEnd);
    onCleanup(() => map.off("zoomend", handleZoomEnd));
  });

  createEffect(() => {
    const map = mapInstance();
    const location = geolocation.location;

    if (isNil(map)) {
      return;
    }

    locationLayer.clearLayers();

    if (!isNil(location)) {
      if (!map.hasLayer(locationLayer)) {
        locationLayer.addTo(map);
      }

      L.circle([location.latitude, location.longitude], {
        radius: location.accuracy,
        color: "#4A90E226",
        fillColor: "#4A90E2",
        fillOpacity: 0.15,
        weight: 1,
      }).addTo(locationLayer);

      L.circleMarker([location.latitude, location.longitude], {
        radius: 8,
        color: "#fff",
        fillColor: "#4A90E2",
        fillOpacity: 1,
        weight: 2,
      }).addTo(locationLayer);
    }

    onCleanup(() => map.removeLayer(locationLayer));
  });

  createEffect(() => {
    const map = mapInstance();
    const config = currentLocationConfiguration();
    if (isNil(map) || isNil(config)) {
      return;
    }

    const layer = L.tileLayer(config.url, {
      attribution: config.attribution,
      maxZoom: config.maxZoom,
    }).addTo(map);

    onCleanup(() => map.removeLayer(layer));
  });

  createEffect(() => {
    const map = mapInstance();
    if (isNil(map)) {
      return;
    }

    const handleMapClick = (event: LeafletMouseEvent) => {
      props.onMapClick?.({
        latitude: event.latlng.lat,
        longitude: event.latlng.lng,
      });
    };

    map.on("click", handleMapClick);
    onCleanup(() => map.off("click", handleMapClick));
  });

  const recenter = () => {
    const loc = geolocation.location;
    if (!isNil(loc)) {
      setLocation(loc);
    }
  };

  const setLocation = (location: ApiCoordinates) => {
    mapInstance()?.setView([location.latitude, location.longitude], DEFAULT_LOCATION_ZOOM);
  };

  const setMarker = (location: ApiCoordinates) => {
    const map = mapInstance();
    if (isNil(map)) {
      return;
    }

    clearMarker();

    if (!map.hasLayer(markerLayer)) {
      markerLayer.addTo(map);
    }

    L.marker([location.latitude, location.longitude], { icon }).addTo(markerLayer);
  };

  const clearMarker = () => {
    markerLayer.clearLayers();
  };

  const currentLocationConfiguration = createMemo<TileConfiguration | undefined>(() => {
    if (isNil(getLocationConfiguration.data)) {
      return;
    }

    return {
      url: getLocationConfiguration.data.tiles[mapStyle()].url,
      attribution: getLocationConfiguration.data.tiles[mapStyle()].attribution,
      maxZoom: getLocationConfiguration.data.tiles[mapStyle()].maxZoom,
    };
  });

  return (
    <Show when={currentLocationConfiguration()}>
      <SolidLeafletMap
        id="map"
        center={currentLocation()}
        width="100%"
        height="100%"
        zoom={DEFAULT_ZOOM}
        mapOptions={{ zoomControl: false }}
        onMapReady={(_, map) => setMapInstance(map)}
      />
    </Show>
  );
};

export default LocationMap;
